import React from "react";

import { Chip, ChipProps, IconButton, Tooltip } from "@mui/material";

/**
 * The affordance that opens a billing form: a labelled chip, or just its icon.
 *
 * The detail panes put these in a table where the label is the only thing
 * naming the action. The dashboard cards already have a field label saying
 * "Billing details", so repeating it in the control is noise - there the same
 * trigger renders as a bare icon with the label kept for the tooltip and
 * screen readers.
 */
export interface PillStatusTriggerProps {
  /** Names the action: the tooltip, and the chip's label when it has one. */
  label: string;
  /** Chip requires an element rather than any node. */
  icon: React.ReactElement;
  testId: string;
  color?: ChipProps["color"];
  /** Chip text colour, matching the icon's. */
  textColor?: string;
  disabled?: boolean;
  /** Drops the text, leaving the icon - for a control under its own label. */
  iconOnly?: boolean;
  onClick?: () => void;
}

const PillStatusTrigger = ({
  label,
  icon,
  testId,
  color = "info",
  textColor,
  disabled,
  iconOnly,
  onClick,
}: PillStatusTriggerProps) => (
  <Tooltip title={label}>
    {iconOnly ? (
      <IconButton
        size="small"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        data-testid={testId}
        sx={{ p: 0.25 }}
      >
        {icon}
      </IconButton>
    ) : (
      <Chip
        variant="outlined"
        size="small"
        label={label}
        color={color}
        disabled={disabled}
        onClick={onClick}
        data-testid={testId}
        icon={icon}
        {...(textColor ? { style: { color: textColor } } : {})}
      />
    )}
  </Tooltip>
);

export default PillStatusTrigger;
